/**
 * Invoice Table Columns
 *
 * Column definitions for the InvoiceList table.
 */

import { Badge } from '@elsapiens/ui'

import {
  normalizeInvoiceType,
  normalizeStatus,
  INVOICE_TYPE_LABELS,
  STATUS_LABELS,
  getTypeBadgeVariant,
  getStatusBadgeVariant,
  formatInvoiceDate,
  computeDueStatus,
  getDueDiffDays,
} from './invoiceListHelpers'
import { formatAmount } from '../../../types/billing'
import {
  normalizePaymentStatus,
  getPaymentStatusBadgeVariant,
  getPaymentStatusDisplayLabel,
} from '../../../utils/billing-helpers'


import type { Invoice } from '../../../types/billing'
import type { TableColumn } from '@elsapiens/ui'

interface BuildColumnsOptions {
  partyLabel: string
  baseCurrency: string
  onView: (invoice: Invoice) => void
  onEdit: (invoice: Invoice) => void
  onGeneratePDF: (invoice: Invoice) => void
  onPost?: (invoice: Invoice) => void
  onVoid?: (invoice: Invoice) => void
}

function InvoiceNumberCell({ inv, onView }: { inv: Invoice; onView: (inv: Invoice) => void }): JSX.Element {
  return (
    <div>
      <button
        className="text-primary hover:underline font-semibold"
        onClick={(e) => { e.stopPropagation(); onView(inv) }}
      >
        {inv.invoiceNumber}
      </button>
      {inv.status === 'posted' && inv.proformaNumber && inv.proformaNumber !== inv.invoiceNumber && (
        <div className="text-xs text-foreground-secondary mt-0.5">
          Draft: {inv.proformaNumber}
        </div>
      )}
    </div>
  )
}

function PartyCell({ inv }: { inv: Invoice }): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="font-medium">{inv.partyName}</span>
      {inv.partyGstin && <span className="text-xs text-foreground-secondary font-mono">{inv.partyGstin}</span>}
    </div>
  )
}

function OutstandingCell({ inv, baseCurrency }: { inv: Invoice; baseCurrency: string }): JSX.Element {
  const status = normalizeStatus(inv.status)
  if (status === 'draft') { return <span className="text-xs text-foreground-secondary">—</span> }
  const total = inv.totalAmount || 0
  const paid = inv.paymentsReceived ?? 0
  const outstanding = total - paid
  if (outstanding <= 0) { return <span className="text-xs text-foreground-secondary">—</span> }
  return (
    <span className="text-orange-600 dark:text-orange-400">
      {formatAmount(outstanding, inv.currency || baseCurrency)}
    </span>
  )
}

function PaymentStatusCell({ inv }: { inv: Invoice }): JSX.Element {
  const status = normalizeStatus(inv.status)
  if (status === 'draft') { return <span className="text-xs text-foreground-secondary">—</span> }
  const ps = normalizePaymentStatus(inv.paymentStatus)
  return <Badge size="sm" variant={getPaymentStatusBadgeVariant(ps)}>{getPaymentStatusDisplayLabel(ps)}</Badge>
}

function DueCell({ inv }: { inv: Invoice }): JSX.Element | null {
  const dueStatus = computeDueStatus(inv)
  if (!dueStatus) { return null }
  if (dueStatus === 'due-today') { return <Badge size="sm" variant="warning">Due today</Badge> }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO(refactor): replace with proper null guard
  const diffDays = getDueDiffDays(inv.dueDate!)
  return <Badge size="sm" variant="error">{diffDays}d overdue</Badge>
}

interface ActionButtonProps {
  title: string
  className: string
  onClick: (e: React.MouseEvent) => void
  children: React.ReactNode
}

function ActionButton({ title, className, onClick, children }: ActionButtonProps): JSX.Element {
  return (
    <button className={className} title={title} onClick={onClick}>
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {children}
      </svg>
    </button>
  )
}

function ViewButton({ invoice, onView }: { invoice: Invoice; onView: (inv: Invoice) => void }): JSX.Element {
  return (
    <ActionButton
      className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 text-foreground-secondary hover:text-blue-600 transition-colors"
      title="View"
      onClick={(e) => { e.stopPropagation(); onView(invoice) }}
    >
      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </ActionButton>
  )
}

function EditButton({ invoice, onEdit }: { invoice: Invoice; onEdit: (inv: Invoice) => void }): JSX.Element {
  return (
    <ActionButton
      className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/30 text-foreground-secondary hover:text-green-600 transition-colors"
      title="Edit"
      onClick={(e) => { e.stopPropagation(); onEdit(invoice) }}
    >
      <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </ActionButton>
  )
}

function DownloadButton({ invoice, onGeneratePDF }: { invoice: Invoice; onGeneratePDF: (inv: Invoice) => void }): JSX.Element {
  return (
    <ActionButton
      className="p-1.5 rounded hover:bg-muted text-foreground-secondary hover:text-primary transition-colors"
      title="Download PDF"
      onClick={(e) => { e.stopPropagation(); onGeneratePDF(invoice) }}
    >
      <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </ActionButton>
  )
}

function PostButton({ invoice, onPost }: { invoice: Invoice; onPost: (inv: Invoice) => void }): JSX.Element {
  return (
    <ActionButton
      className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/30 text-foreground-secondary hover:text-green-600 transition-colors"
      title="Post Invoice"
      onClick={(e) => { e.stopPropagation(); onPost(invoice) }}
    >
      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </ActionButton>
  )
}

function VoidButton({ invoice, onVoid }: { invoice: Invoice; onVoid: (inv: Invoice) => void }): JSX.Element {
  return (
    <ActionButton
      className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-foreground-secondary hover:text-red-600 transition-colors"
      title="Void Invoice"
      onClick={(e) => { e.stopPropagation(); onVoid(invoice) }}
    >
      <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </ActionButton>
  )
}

interface ActionButtonsProps {
  invoice: Invoice
  onView: (inv: Invoice) => void
  onEdit: (inv: Invoice) => void
  onGeneratePDF: (inv: Invoice) => void
  onPost?: (inv: Invoice) => void
  onVoid?: (inv: Invoice) => void
}

function ActionButtons({ invoice, onView, onEdit, onGeneratePDF, onPost, onVoid }: ActionButtonsProps): JSX.Element {
  const status = normalizeStatus(invoice.status)
  const canEdit = status === 'draft' || (status === 'posted' && !invoice.irn && !invoice.ewayBillNumber)
  return (
    <div className="flex items-center gap-1">
      <ViewButton invoice={invoice} onView={onView} />
      {canEdit && <EditButton invoice={invoice} onEdit={onEdit} />}
      <DownloadButton invoice={invoice} onGeneratePDF={onGeneratePDF} />
      {status === 'draft' && onPost && <PostButton invoice={invoice} onPost={onPost} />}
      {status === 'posted' && onVoid && <VoidButton invoice={invoice} onVoid={onVoid} />}
    </div>
  )
}

function buildNumberDateTypeCols(partyLabel: string, onView: (inv: Invoice) => void): TableColumn<Invoice>[] {
  return [
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      render: (inv: Invoice) => <InvoiceNumberCell inv={inv} onView={onView} />,
    },
    {
      key: 'invoiceDate',
      header: 'Date',
      render: (inv: Invoice) => formatInvoiceDate(inv.invoiceDate),
    },
    {
      key: 'invoiceType',
      header: 'Type',
      render: (inv: Invoice) => {
        const type = normalizeInvoiceType(inv.invoiceType)
        return <Badge size="sm" variant={getTypeBadgeVariant(type)}>{INVOICE_TYPE_LABELS[type] ?? inv.invoiceType}</Badge>
      },
    },
    {
      key: 'partyName',
      header: partyLabel,
      render: (inv: Invoice) => <PartyCell inv={inv} />,
    },
  ]
}

function buildAmountCols(baseCurrency: string): TableColumn<Invoice>[] {
  return [
    {
      key: 'taxableAmount',
      header: 'Taxable',
      align: 'right',
      className: 'font-mono',
      render: (inv: Invoice) => formatAmount(inv.taxableAmount || 0, inv.currency || baseCurrency),
    },
    {
      key: 'tax',
      header: 'Tax',
      align: 'right',
      className: 'font-mono',
      render: (inv: Invoice) => {
        const totalTax = (Number(inv.cgstAmount) || 0) + (Number(inv.sgstAmount) || 0) + (Number(inv.igstAmount) || 0)
        return formatAmount(totalTax, inv.currency || baseCurrency)
      },
    },
    {
      key: 'totalAmount',
      header: 'Total',
      align: 'right',
      className: 'font-mono font-semibold',
      render: (inv: Invoice) => formatAmount(inv.totalAmount || 0, inv.currency || baseCurrency),
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      align: 'right',
      className: 'font-mono',
      render: (inv: Invoice) => <OutstandingCell baseCurrency={baseCurrency} inv={inv} />,
    },
  ]
}

function buildStatusCols(): TableColumn<Invoice>[] {
  return [
    { key: 'paymentStatus', header: 'Payment', render: (inv: Invoice) => <PaymentStatusCell inv={inv} /> },
    { key: 'due', header: 'Due', render: (inv: Invoice) => <DueCell inv={inv} /> },
    {
      key: 'status',
      header: 'Status',
      render: (inv: Invoice) => {
        const status = normalizeStatus(inv.status)
        return <Badge size="sm" variant={getStatusBadgeVariant(status)}>{STATUS_LABELS[status] ?? inv.status}</Badge>
      },
    },
  ]
}

export function buildInvoiceColumns(opts: BuildColumnsOptions): TableColumn<Invoice>[] {
  const { partyLabel, baseCurrency, onView, onEdit, onGeneratePDF, onPost, onVoid } = opts
  return [
    ...buildNumberDateTypeCols(partyLabel, onView),
    ...buildAmountCols(baseCurrency),
    ...buildStatusCols(),
    {
      key: 'actions',
      header: 'Actions',
      width: '180px',
      render: (invoice: Invoice) => (
        <ActionButtons
          invoice={invoice}
          onEdit={onEdit}
          onGeneratePDF={onGeneratePDF}
          onPost={onPost}
          onView={onView}
          onVoid={onVoid}
        />
      ),
    },
  ]
}
